import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, breadcrumbSchema, pageHead } from "@/lib/seo";
import { SITE_URL } from "@/lib/locale";

export const Route = createFileRoute("/leistungen/performance-marketing/meta-ads/recruiting")({
  head: () => {
    const h = pageHead("pmMetaAdsRecruiting", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Meta Ads für Mitarbeiter-Gewinnung",
          serviceType: "Recruiting Advertising",
          description:
            "Meta Ads für Mitarbeiter-Gewinnung in Pflege, Handwerk, Logistik, Produktion, Gastronomie und pädagogischen Berufen. 60–90 % niedrigere CPA als klassische Stellen-Portale.",
          url: `${SITE_URL}/leistungen/performance-marketing/meta-ads/recruiting`,
          provider: {
            "@type": "Organization",
            name: "Calvarius GmbH",
            url: SITE_URL,
          },
          areaServed: [
            "Main-Spessart",
            "Würzburg",
            "Karlstadt am Main",
            "Aschaffenburg",
            "Frankfurt am Main",
            "Deutschland",
          ],
          audience: {
            "@type": "BusinessAudience",
            audienceType:
              "Mittelständische Unternehmen mit Mitarbeiter-Bedarf in Pflege, Handwerk, Logistik, Produktion, Gastronomie, pädagogischen Berufen",
          },
          isPartOf: {
            "@type": "Service",
            name: "Meta Ads",
            url: `${SITE_URL}/leistungen/performance-marketing/meta-ads`,
          },
        }),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Start", path: "/" },
            { name: "Leistungen", path: "/leistungen" },
            { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
            { name: "Meta Ads", path: "/leistungen/performance-marketing/meta-ads" },
            { name: "Recruiting", path: "/leistungen/performance-marketing/meta-ads/recruiting" },
          ]),
        ),
      ],
    };
  },
  component: MetaAdsRecruitingDE,
});

const steps = [
  {
    num: "01",
    title: "Zielgruppen-Analyse (3–5 Tage)",
    body: "Definition des Such-Profils gemeinsam mit der Personalabteilung: Berufsbild, geografisches Einzugsgebiet, demografische Marker, Mediennutzungs-Verhalten. Audit bestehender Recruiting-Kanäle (Stellen-Portale, Karriere-Seite, Empfehlungsprogramm) auf Cost-per-Application und Qualitäts-Quote. Klare Hypothesen, wo Meta Ads wirtschaftlich besser performt.",
  },
  {
    num: "02",
    title: "Funnel- & Landing-Setup (1–2 Wochen)",
    body: "Aufbau einer dedizierten Bewerbungs-Landingpage — kein PDF-Download, sondern niederschwelliges Formular mit zwei bis vier Feldern. Conversions-API-Anbindung für Lead-Tracking, mobile-first Design, Vertrauens-Signale (Standort, Team-Bilder, Benefits). Klassische Karriere-Seiten konvertieren bei Mobile-Recruiting-Traffic schlecht — wir bauen eine spezifische Funnel-Stufe.",
  },
  {
    num: "03",
    title: "Kampagnen-Launch & erste Iteration (Woche 3–4)",
    body: "Geo-Targeting auf das definierte Einzugsgebiet, demografische Schichten, Custom Audiences aus bestehenden Mitarbeiter-Profilen für Lookalike-Modelle. Creative-Variation in Markentonalität via picazzoo. Erste Lerndaten nach 7–10 Tagen, erste Optimierungs-Welle nach 14 Tagen.",
  },
  {
    num: "04",
    title: "Skalierung & Pflege (kontinuierlich)",
    body: "Kontinuierliche Creative-Iteration auf das, was Bewerbungen produziert — nicht auf das, was Klicks produziert. Saisonale Anpassung an Bewerber-Verhalten. Monatliches Reporting auf Cost-per-Application und Cost-per-Hire, nicht auf Plattform-CPCs. Operative Steuerung direkt durch den Geschäftsführer.",
  },
];

const capabilities = [
  { name: "Geo-Targeting auf Einzugsgebiet", detail: "Radius · PLZ · Standort" },
  { name: "Demografische Profilbildung", detail: "Alter · Beruf · Interessen" },
  { name: "Lookalike-Modelle", detail: "Aus bestehenden Mitarbeiter-Profilen" },
  { name: "Bewerbungs-Landingpages", detail: "Mobile-first · niederschwellig" },
  { name: "Conversions API für Leads", detail: "Server-side · Match Quality" },
  { name: "Creative-Produktion via picazzoo", detail: "Markentreu · skalierbar" },
  { name: "Industry-Benchmarks CPA", detail: "Pflege · Handwerk · Logistik" },
  { name: "Reporting auf Cost-per-Hire", detail: "Wirtschaftlich, nicht Plattform" },
];

const audience = [
  { tag: "Primär", title: "Pflege & Gesundheit", body: "Pflegeheime, ambulante Pflegedienste, Krankenhäuser, MVZs. Klassische Stellen-Portale liefern bei Pflegekräften zunehmend leere Schächte — Meta Ads erreicht die Zielgruppe da, wo sie tatsächlich Zeit verbringt." },
  { tag: "Primär", title: "Handwerk & Bau", body: "Elektriker, Sanitär-Heizung-Klima, Tischler, Maurer, Maler. Hohe Visualität (Werkstatt, Projekte), klare regionale Bindung — beides ideal für Meta Ads." },
  { tag: "Primär", title: "Logistik, Produktion, Gastronomie", body: "Fahrer, Lager, Schicht-Produktion, Service, Küche. Großes Bewerber-Volumen mit klaren geografischen und demografischen Markern. Meta Ads ist hier oft günstiger als jede Print-Anzeige." },
  { tag: "Primär", title: "Pädagogische Berufe", body: "Kita, Hort, Tagespflege, Sozialarbeit. Zielgruppe ist auf Meta hoch aktiv — Karriere-Seiten und Stellen-Portale unterperformen massiv." },
];

const realistic = [
  "Cost-per-Application 60–90 % unter klassischen Stellen-Portalen ist in den meisten Setups erreichbar — vor allem in Berufsfeldern mit hohem Stellenwettbewerb (Pflege, Handwerk, Logistik).",
  "Bewerber-Volumen-Steigerung um den Faktor 3–8x gegenüber alleiniger Karriere-Seite ist realistisch, wenn die Landing-Page sauber konvertiert.",
  "Time-to-Hire-Reduktion um 30–50 % durch konstanten Bewerber-Fluss statt Wartezeiten auf Portal-Zyklen.",
  "Wirtschaftlich ab ca. €800/Monat Media-Budget — darunter wird der operative Aufwand pro Bewerbung zu hoch.",
];

const value = [
  ["Regionale Treffsicherheit statt nationaler Streuung.", "Meta Ads ermöglicht Geo-Targeting auf den exakten Einzugsbereich — kein Bewerbungs-Spam aus 200 km Entfernung."],
  ["Sichtbarkeit dort, wo die Zielgruppe Zeit verbringt.", "Pflegekräfte, Handwerker und Logistik-Personal nutzen Stellen-Portale selten aktiv — auf Instagram und Facebook sind sie täglich."],
  ["Niederschwelliger Bewerbungs-Funnel.", "Statt PDF-Lebenslauf hochladen: Name, Kontakt, eine Frage. Bewerbungen kommen an, weil die Hürde niedrig ist."],
  ["Bewerber-Qualität durch Targeting steuerbar.", "Falsche Bewerbungen filtern wir nicht durch HR-Aufwand, sondern durch Targeting-Disziplin und Funnel-Logik."],
  ["Monatlich kalkulierbar statt Stellen-Portal-Pakete.", "Sie zahlen für tatsächlich erreichte Zielgruppe, nicht für Portal-Slots, die niemand sieht."],
  ["Kein Set-and-Forget.", "Recruiting-Performance verändert sich mit Saison, Wettbewerb und Markt — wir steuern aktiv mit."],
];

const related = [
  { to: "/leistungen/performance-marketing/meta-ads", name: "Meta Ads (Hauptseite)", sub: "Performance-Marketing-Mandate auf Meta für E-Commerce, B2C-SaaS und B2B" },
  { to: "/leistungen/conversion-rate-optimierung", name: "Conversion-Rate-Optimierung", sub: "Bewerbungs-Landingpages mit klarer Funnel-Logik" },
  { to: "/leistungen/picazzoo", name: "picazzoo", sub: "Creative-Produktion für Recruiting-Anzeigen in Markentonalität" },
];

function MetaAdsRecruitingDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Meta Ads · Recruiting"
          headline={<>Meta Ads für Mitarbeiter-Gewinnung — <em>Recruiting ohne Streuverluste,</em> mit Targeting-Disziplin.</>}
          lead="Klassische Stellen-Portale verlieren in vielen Berufsfeldern an Wirkung — Pflege, Handwerk, Logistik, Produktion, Gastronomie und pädagogische Berufe finden ihre Bewerber heute auf Instagram und Facebook. Calvarius betreibt Meta Ads für Recruiting seit 2018 als eigenständige Disziplin: mit Geo-Targeting auf den realen Einzugsbereich, niederschwelligen Bewerbungs-Funnels und 60–90 Prozent niedrigerer Cost-per-Application als klassische Portale."
          metaStrip={[
            "Seit 2018 im Recruiting-Kanal",
            "Ab €800 Media-Budget wirtschaftlich",
            "Bis 90 % niedrigere CPA vs. klassisch",
          ]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "Meta Ads", to: "/leistungen/performance-marketing/meta-ads" },
            { label: "Recruiting" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Warum klassisches Recruiting <em>in vielen Berufen nicht mehr greift.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Die Recruiting-Realität in Pflege, Handwerk, Logistik und Produktion hat sich in den letzten Jahren still verschoben. Stellen-Portale wie Stepstone, Indeed oder Monster wurden ursprünglich für Wissensarbeiter gebaut — Menschen, die aktiv nach Stellen suchen, einen Lebenslauf bereit halten und auf einer Desktop-Plattform durch Listings scrollen. In Berufen mit hohem Stellenwettbewerb suchen die meisten qualifizierten Kräfte nicht aktiv. Sie sind beschäftigt, sie sind vor Ort, und sie verbringen ihre Aufmerksamkeits-Zeit nicht auf Karriere-Portalen — sondern auf Instagram, Facebook und in Messaging-Diensten.</p>
              <p className="mb-5">Meta Ads erreicht diese Zielgruppen, weil die Plattform pro Tag mehrere Stunden Aufmerksamkeit bekommt — und weil Geo-, Alters- und Interessens-Targeting eine Präzision erlauben, die kein Stellen-Portal bieten kann. Das macht Meta Ads zur wirtschaftlich rationalsten Recruiting-Infrastruktur für mittelständische Mandate, deren Bewerber-Bedarf saisonal, regional und qualitativ präzise gesteuert werden muss.</p>
              <p>Calvarius betreibt Recruiting über Meta Ads seit 2018 als eigenständige Disziplin innerhalb des Meta-Ökosystems. Wir verstehen die spezifischen Mechaniken, die ein Bewerbungs-Funnel braucht — eine Bewerbungs-Landingpage ist keine Verkaufs-Landingpage, ein Recruiting-Creative ist kein Performance-Creative, und das passende <Link to="/glossar/attribution-modell" className="underline">Attributions-Modell</Link> für Bewerbungen ist ein anderes als für E-Commerce-Conversions.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Für wen Meta Ads Recruiting <em>wirtschaftlich Sinn macht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie wir Recruiting-Mandate <em>aufbauen.</em></>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unser <em>Recruiting-Setup</em> konkret enthält.</>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse <em>im Recruiting-Mandat.</em></>}
          caption="Diese Bandbreiten sind Erfahrungswerte aus Mandaten in Pflege, Handwerk, Logistik und Gastronomie.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was Sie gewinnen" headline={<>Was Sie von <em>Meta Ads Recruiting</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing/meta-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Zurück zur Meta-Ads-Hauptseite
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="09" kicker="Konkret werden">
          <CTABlock
            headline={<>Recruiting-Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob Meta Ads für Ihren Personal-Bedarf wirtschaftlich Sinn macht — und wenn ja, mit welchem Setup. Kostenlos, unverbindlich, ohne Sales-Pitch."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Zurück zu Meta Ads", to: "/leistungen/performance-marketing/meta-ads" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
